import { useEffect, useState } from "react";
import { fetchCommunityAssetBlob } from "../../api/community";

/**
 * Resolve an auth-gated Community asset URL (a `/images/:id` path, or the
 * legacy `/api/community/assets/:id` path) into an object URL an `<img>` can
 * render, with explicit loading and error states.
 *
 * The settings page's `ProfileImageCard` uses this to render the caller's
 * avatar (their `community_profiles.avatar_url` points at the asset route).
 * Feeds and moment images use `CommunityAssetImage` for the same reason;
 * this hook is the slimmer avatar-shape variant.
 *
 * Cleans up its object URL on unmount or when the input URL changes so the
 * browser does not leak them on long-lived surfaces, and aborts the
 * underlying fetch so an in-flight load doesn't keep a connection to the
 * asset host open after the caller stops caring about the result.
 */

export type AuthGatedImageState =
  | { status: "idle"; url: null }
  | { status: "loading"; url: null }
  | { status: "loaded"; url: string }
  | { status: "error"; url: null };

const MAX_CACHED_ASSETS = 24;
type CachedAsset = {
  consumers: number;
  objectUrl: string;
};

const cachedAssetUrls = new Map<string, CachedAsset>();

export function useAuthGatedImageUrl(assetUrl: string | null | undefined): AuthGatedImageState {
  const [state, setState] = useState<AuthGatedImageState>(() => stateForAsset(assetUrl));

  useEffect(() => {
    if (!assetUrl) {
      setState({ status: "idle", url: null });
      return;
    }

    const cachedAsset = cachedAssetUrls.get(assetUrl);
    if (cachedAsset) {
      cachedAsset.consumers += 1;
      promoteCachedAsset(assetUrl, cachedAsset);
      setState({ status: "loaded", url: cachedAsset.objectUrl });
      return () => releaseCachedAsset(assetUrl, cachedAsset);
    }

    let active = true;
    let cacheEntry: CachedAsset | null = null;
    const controller = new AbortController();
    setState({ status: "loading", url: null });

    void fetchCommunityAssetBlob(assetUrl, controller.signal)
      .then((blob) => {
        if (!active) return;
        const objectUrl = URL.createObjectURL(blob);
        const existingAsset = cachedAssetUrls.get(assetUrl);
        if (existingAsset) {
          URL.revokeObjectURL(objectUrl);
          existingAsset.consumers += 1;
          cacheEntry = existingAsset;
          promoteCachedAsset(assetUrl, existingAsset);
          setState({ status: "loaded", url: existingAsset.objectUrl });
          return;
        }
        cacheEntry = cacheAsset(assetUrl, objectUrl);
        setState({ status: "loaded", url: objectUrl });
      })
      .catch(() => {
        if (!active) return;
        setState({ status: "error", url: null });
      });

    return () => {
      active = false;
      controller.abort();
      if (cacheEntry) releaseCachedAsset(assetUrl, cacheEntry);
    };
  }, [assetUrl]);

  return state;
}

/** True for a Community asset path that needs a bearer token to fetch. */
export function isGatedAssetUrl(url: string | null | undefined): url is string {
  return Boolean(url?.startsWith("/images/") || url?.startsWith("/api/community/assets/"));
}

/**
 * Resolve several gated asset URLs at once, keyed by the original path.
 *
 * Leaflet markers are built as raw HTML strings, so each avatar has to be an
 * object URL before the marker is created; a per-URL hook cannot be called
 * inside that loop. Non-gated and unresolved URLs are simply absent from the
 * returned map, so a caller falls back to initials.
 */
export function useAuthGatedImageUrls(
  assetUrls: ReadonlyArray<string | null | undefined>,
): Map<string, string> {
  const cacheKey = [...new Set(assetUrls.filter(isGatedAssetUrl))].sort().join("|");
  const [resolved, setResolved] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    const urls = cacheKey ? cacheKey.split("|") : [];
    if (!urls.length) {
      setResolved(new Map());
      return;
    }
    let active = true;
    const controller = new AbortController();
    const held: Array<[string, CachedAsset]> = [];

    const load = async (assetUrl: string): Promise<[string, string]> => {
      const cachedAsset = cachedAssetUrls.get(assetUrl);
      if (cachedAsset) {
        cachedAsset.consumers += 1;
        promoteCachedAsset(assetUrl, cachedAsset);
        held.push([assetUrl, cachedAsset]);
        return [assetUrl, cachedAsset.objectUrl];
      }
      const blob = await fetchCommunityAssetBlob(assetUrl, controller.signal);
      const objectUrl = URL.createObjectURL(blob);
      const existingAsset = cachedAssetUrls.get(assetUrl);
      if (existingAsset) {
        URL.revokeObjectURL(objectUrl);
        existingAsset.consumers += 1;
        promoteCachedAsset(assetUrl, existingAsset);
        held.push([assetUrl, existingAsset]);
        return [assetUrl, existingAsset.objectUrl];
      }
      held.push([assetUrl, cacheAsset(assetUrl, objectUrl)]);
      return [assetUrl, objectUrl];
    };

    void Promise.all(urls.map((assetUrl) => load(assetUrl).catch(() => null))).then((entries) => {
      if (!active) return;
      setResolved(new Map(entries.filter((entry): entry is [string, string] => entry !== null)));
    });

    return () => {
      active = false;
      controller.abort();
      for (const [assetUrl, asset] of held) releaseCachedAsset(assetUrl, asset);
    };
  }, [cacheKey]);

  return resolved;
}

function stateForAsset(assetUrl: string | null | undefined): AuthGatedImageState {
  if (!assetUrl) return { status: "idle", url: null };
  const cachedAsset = cachedAssetUrls.get(assetUrl);
  if (!cachedAsset) return { status: "idle", url: null };
  promoteCachedAsset(assetUrl, cachedAsset);
  return { status: "loaded", url: cachedAsset.objectUrl };
}

function cacheAsset(assetUrl: string, objectUrl: string): CachedAsset {
  const asset = { objectUrl, consumers: 1 };
  cachedAssetUrls.set(assetUrl, asset);
  trimCachedAssets();
  return asset;
}

function releaseCachedAsset(assetUrl: string, asset: CachedAsset): void {
  asset.consumers = Math.max(0, asset.consumers - 1);
  promoteCachedAsset(assetUrl, asset);
  trimCachedAssets();
}

function trimCachedAssets(): void {
  while (cachedAssetUrls.size > MAX_CACHED_ASSETS) {
    const evictableAsset = [...cachedAssetUrls.entries()].find(
      ([, asset]) => asset.consumers === 0,
    );
    if (!evictableAsset) return;
    const [assetUrl, asset] = evictableAsset;
    cachedAssetUrls.delete(assetUrl);
    URL.revokeObjectURL(asset.objectUrl);
  }
}

function promoteCachedAsset(assetUrl: string, asset: CachedAsset): void {
  cachedAssetUrls.delete(assetUrl);
  cachedAssetUrls.set(assetUrl, asset);
}
